'use client';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { CademyError } from '@shared/domain-shared';
import { CademyAPI } from '..';
import {
    OperationId,
    InputsOfOperation,
    SuccessResultOfOperation,
} from '@cademy-package/client-sdk';

export type UseCademyAPIMutationOptions<TOperationId extends OperationId> = Omit<
    UseMutationOptions<
        SuccessResultOfOperation<TOperationId>,
        CademyError,
        InputsOfOperation<TOperationId>
    >,
    'mutationFn'
>;

export const useCademyAPIMutation = <TOperationId extends OperationId>(
    operation: TOperationId,
    options?: UseCademyAPIMutationOptions<TOperationId>
) => {
    const mutationFn = useCallback(
        async (
            input: InputsOfOperation<OperationId>
        ): Promise<SuccessResultOfOperation<OperationId>> => {
            try {
                const response = await CademyAPI(operation)(input as any);
                if (response.statusCode === 204) {
                    return undefined as SuccessResultOfOperation<TOperationId>;
                }
                if (response.statusCode === 200) {
                    return response.body as SuccessResultOfOperation<TOperationId>;
                }
                throw CademyError.fromUnknown(response.body);
            } catch (error) {
                if (error instanceof CademyError) {
                    throw error;
                }
                console.error(error);
                throw CademyError.fromUnknown(error);
            }
        },
        [operation]
    );

    const result = useMutation({
        // @ts-expect-error Typing of useMutation is a bit overloaded so it can't work out that this is a valid mutation function
        mutationFn,
        ...options,
    });

    return result;
};
